import React, { useState } from 'react';
import { Form, Input, Button, Select, Divider, Card, Space, Collapse } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;

const FunctionForm = ({ initialValues, onSubmit, onCancel }) => {
  const [form] = Form.useForm();
  const [authType, setAuthType] = useState(initialValues?.authType || 'none');
  const [parameters, setParameters] = useState(initialValues?.parameters || []);
  const [headers, setHeaders] = useState(
    initialValues?.headers ? 
      Object.entries(initialValues.headers).map(([key, value]) => ({ key, value })) : 
      []
  );

  const handleSubmit = (values) => {
    // Convert headers array to object
    const headersObj = {};
    headers.forEach(header => {
      if (header.key && header.value) {
        headersObj[header.key] = header.value;
      }
    });
    
    const payload = {
      ...values,
      parameters,
      headers: headersObj,
      authConfig: buildAuthConfig(values, authType)
    };
    
    // Clean up form values that are now in authConfig
    delete payload.bearerToken;
    delete payload.apiKey;
    delete payload.apiKeyHeader;
    delete payload.username;
    delete payload.password;
    
    onSubmit(payload);
  };

  const buildAuthConfig = (values, type) => {
    const config = {};
    
    if (type === 'bearer') {
      config.token = values.bearerToken;
    } else if (type === 'api_key') {
      config.apiKey = values.apiKey;
      config.apiKeyHeader = values.apiKeyHeader || 'X-API-Key';
    } else if (type === 'basic_auth') {
      config.username = values.username;
      config.password = values.password;
    } else if (type === 'custom_header' && initialValues?.authConfig?.customHeaders) {
      config.customHeaders = initialValues.authConfig.customHeaders;
    }
    
    return config;
  };

  const addParameter = () => {
    setParameters([...parameters, {
      name: '',
      type: 'string',
      description: '',
      required: false,
      location: 'body'
    }]);
  };

  const updateParameter = (index, field, value) => {
    const newParams = [...parameters];
    newParams[index][field] = value;
    setParameters(newParams);
  };

  const removeParameter = (index) => {
    setParameters(parameters.filter((_, i) => i !== index));
  };

  const addHeader = () => {
    setHeaders([...headers, { key: '', value: '' }]);
  };

  const updateHeader = (index, field, value) => {
    const newHeaders = [...headers];
    newHeaders[index][field] = value;
    setHeaders(newHeaders);
  };

  const removeHeader = (index) => {
    setHeaders(headers.filter((_, i) => i !== index));
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        ...initialValues,
        bearerToken: initialValues?.authConfig?.token,
        apiKey: initialValues?.authConfig?.apiKey,
        apiKeyHeader: initialValues?.authConfig?.apiKeyHeader,
        username: initialValues?.authConfig?.username,
        password: initialValues?.authConfig?.password,
      }}
    >
      <Form.Item
        label="Function ID"
        name="functionId"
        rules={[
          { required: true, message: 'Please enter a function ID' },
          { pattern: /^[a-z0-9-_]+$/, message: 'Use only lowercase letters, numbers, hyphens, and underscores' }
        ]}
      >
        <Input 
          placeholder="e.g., get-weather-data" 
        />
      </Form.Item>

      <Form.Item
        label="Function Name"
        name="name"
        rules={[{ required: true, message: 'Please enter a function name' }]}
      >
        <Input placeholder="e.g., Get Weather Data" />
      </Form.Item>

      <Form.Item
        label="Description"
        name="description"
        rules={[{ required: true, message: 'Please enter a description' }]}
        extra="Describe what this function does and when to use it"
      >
        <TextArea 
          rows={3}
          placeholder="This function retrieves weather data for a given location..."
        />
      </Form.Item>

      <Form.Item
        label="Category"
        name="category"
        rules={[{ required: true, message: 'Please select a category' }]}
      >
        <Select placeholder="Select category">
          <Option value="Custom">Custom</Option>
          <Option value="External API">External API</Option>
          <Option value="Data Retrieval">Data Retrieval</Option>
          <Option value="Notifications">Notifications</Option>
          <Option value="Utilities">Utilities</Option>
          <Option value="Integration">Integration</Option>
        </Select>
      </Form.Item>

      <Divider>HTTP Configuration</Divider>

      <Form.Item
        label="HTTP Method"
        name="httpMethod"
        rules={[{ required: true, message: 'Please select HTTP method' }]}
      >
        <Select placeholder="Select HTTP method">
          <Option value="GET">GET</Option>
          <Option value="POST">POST</Option>
          <Option value="PUT">PUT</Option>
          <Option value="PATCH">PATCH</Option>
          <Option value="DELETE">DELETE</Option>
        </Select>
      </Form.Item>

      <Form.Item
        label="Endpoint URL"
        name="endpoint"
        rules={[
          { required: true, message: 'Please enter endpoint URL' },
          { type: 'url', message: 'Please enter a valid URL' }
        ]}
        extra="Use {paramName} for path parameters, e.g., https://api.example.com/users/{userId}"
      >
        <Input placeholder="https://api.example.com/endpoint" />
      </Form.Item>

      <Divider>Custom Headers</Divider>

      <div style={{ marginBottom: 16 }}>
        <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
          Add custom HTTP headers (e.g., Authorization, Content-Type, custom headers)
        </p>
        <Button 
          type="dashed" 
          onClick={addHeader} 
          icon={<PlusOutlined />}
          block
        >
          Add Header
        </Button>
      </div>

      {headers.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {headers.map((header, index) => (
            <Card 
              key={index}
              size="small"
              style={{ marginBottom: 12 }}
              extra={
                <DeleteOutlined 
                  onClick={() => removeHeader(index)}
                  style={{ color: 'red', cursor: 'pointer' }}
                />
              }
            >
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <div>
                  <label style={{ fontWeight: 600, marginBottom: 4, display: 'block', fontSize: 12 }}>
                    Header Name
                  </label>
                  <Input
                    placeholder="e.g., Authorization, X-API-Key"
                    value={header.key}
                    onChange={(e) => updateHeader(index, 'key', e.target.value)}
                  />
                </div>
                <div>
                  <label style={{ fontWeight: 600, marginBottom: 4, display: 'block', fontSize: 12 }}>
                    Header Value
                  </label>
                  <Input
                    placeholder="e.g., Bearer token123, or custom value"
                    value={header.value}
                    onChange={(e) => updateHeader(index, 'value', e.target.value)}
                  />
                </div>
              </Space>
            </Card>
          ))}
        </div>
      )}

      <Divider>Authentication (Legacy - Prefer Headers Above)</Divider>

      <Form.Item
        label="Authentication Type"
        name="authType"
        rules={[{ required: true, message: 'Please select authentication type' }]}
      >
        <Select 
          placeholder="Select authentication type"
          onChange={(value) => setAuthType(value)}
        >
          <Option value="none">None</Option>
          <Option value="bearer">Bearer Token</Option>
          <Option value="api_key">API Key</Option>
          <Option value="basic_auth">Basic Auth</Option>
          <Option value="custom_header">Custom Headers</Option>
        </Select>
      </Form.Item>

      {authType === 'bearer' && (
        <Form.Item
          label="Bearer Token"
          name="bearerToken"
          rules={[{ required: true, message: 'Please enter bearer token' }]}
        >
          <Input.Password placeholder="Enter bearer token" />
        </Form.Item>
      )}

      {authType === 'api_key' && (
        <>
          <Form.Item
            label="API Key"
            name="apiKey"
            rules={[{ required: true, message: 'Please enter API key' }]}
          >
            <Input.Password placeholder="Enter API key" />
          </Form.Item>
          <Form.Item
            label="API Key Header Name"
            name="apiKeyHeader"
            extra="Default is 'X-API-Key'"
          >
            <Input placeholder="X-API-Key" />
          </Form.Item>
        </>
      )}

      {authType === 'basic_auth' && (
        <>
          <Form.Item
            label="Username"
            name="username"
            rules={[{ required: true, message: 'Please enter username' }]}
          >
            <Input placeholder="Enter username" />
          </Form.Item>
          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: 'Please enter password' }]}
          >
            <Input.Password placeholder="Enter password" />
          </Form.Item>
        </>
      )}

      <Divider>Parameters</Divider>

      <div style={{ marginBottom: 16 }}>
        <Button 
          type="dashed" 
          onClick={addParameter} 
          icon={<PlusOutlined />}
          block
        >
          Add Parameter
        </Button>
      </div>

      {parameters.length > 0 && (
        <Collapse style={{ marginBottom: 16 }}>
          {parameters.map((param, index) => (
            <Panel 
              header={`Parameter ${index + 1}: ${param.name || '(unnamed)'}`} 
              key={index}
              extra={
                <DeleteOutlined 
                  onClick={(e) => {
                    e.stopPropagation();
                    removeParameter(index);
                  }}
                  style={{ color: 'red' }}
                />
              }
            >
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>
                  <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>
                    Parameter Name *
                  </label>
                  <Input
                    placeholder="e.g., location"
                    value={param.name}
                    onChange={(e) => updateParameter(index, 'name', e.target.value)}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>
                    Type *
                  </label>
                  <Select
                    value={param.type}
                    onChange={(value) => updateParameter(index, 'type', value)}
                    style={{ width: '100%' }}
                  >
                    <Option value="string">String</Option>
                    <Option value="number">Number</Option>
                    <Option value="boolean">Boolean</Option>
                    <Option value="array">Array</Option>
                    <Option value="object">Object</Option>
                  </Select>
                </div>

                <div>
                  <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>
                    Location *
                  </label>
                  <Select
                    value={param.location}
                    onChange={(value) => updateParameter(index, 'location', value)}
                    style={{ width: '100%' }}
                  >
                    <Option value="query">Query Parameter</Option>
                    <Option value="body">Request Body</Option>
                    <Option value="path">Path Parameter</Option>
                  </Select>
                </div>

                <div>
                  <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>
                    Description
                  </label>
                  <Input
                    placeholder="Describe this parameter"
                    value={param.description}
                    onChange={(e) => updateParameter(index, 'description', e.target.value)}
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={param.required}
                      onChange={(e) => updateParameter(index, 'required', e.target.checked)}
                      style={{ marginRight: 8 }}
                    />
                    <span>Required parameter</span>
                  </label>
                </div>
              </Space>
            </Panel>
          ))}
        </Collapse>
      )}

      <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
        <Button type="default" onClick={onCancel} style={{ marginRight: 8 }}>
          Cancel
        </Button>
        <Button type="primary" htmlType="submit">
          {initialValues ? 'Update Function' : 'Create Function'}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default FunctionForm;

